import { useEffect, useMemo, useRef, useState } from "react";
import { usePharmaFormFields } from "../context/pharmaFormFieldsContext";
import {
  extractTemplateKeys,
  fetchOptionsFromPath,
  isDynamicPath,
  resolveTemplatePath,
} from "../pages/pharmaPaForm/utils/refDocPathUtils";

interface UseDynamicRefDocOptionsReturn {
  /** Resolved options fetched from the dynamic Firestore path. */
  options: { label: string; value: string }[];
  /** `true` while a Firestore fetch is in-flight. */
  isLoading: boolean;
}

/**
 * Reactively fetches dropdown/radio-button options from a Firestore document
 * whose path contains template variables (e.g. `${field_key}`).
 *
 * Template variables are resolved against the current `formFieldsData` in
 * `PharmaFormFieldsContext`. When any referenced field value changes the path
 * is re-resolved and new options are fetched automatically.
 *
 * For paths that do **not** contain template variables the hook is inactive
 * and returns an empty options array — static paths are resolved at form-load
 * time by `replaceOptionsWithRefDocId` instead.
 *
 * If a dependent field changes while a fetch is still in-flight, the stale
 * fetch result is discarded so only the most recent response is applied.
 *
 * @param refDocPath      - Firestore document path, optionally containing
 *                          `${field_key}` template variables.
 * @param refDocFieldName - Field name inside the document that holds the
 *                          options array. Defaults to `"data"`.
 * @returns `{ options, isLoading }` — the fetched options and loading state.
 */
export const useDynamicRefDocOptions = (
  refDocPath?: string,
  refDocFieldName?: string,
): UseDynamicRefDocOptionsReturn => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const lastResolvedPathRef = useRef<string | null>(null);
  const { formFieldsData } = usePharmaFormFields();
  const formFieldsDataRef = useRef(formFieldsData);
  formFieldsDataRef.current = formFieldsData;

  const isActive = !!refDocPath && isDynamicPath(refDocPath);

  const dependentValuesKey = useMemo(() => {
    if (!isActive) return "";
    const keys = extractTemplateKeys(refDocPath!);
    const values = keys.map(
      (key) => (formFieldsData as Record<string, any>)[key]?.filledValue ?? "",
    );
    return JSON.stringify(values);
  }, [isActive, refDocPath, formFieldsData]);

  useEffect(() => {
    if (!isActive) return;

    const resolved = resolveTemplatePath(
      refDocPath!,
      formFieldsDataRef.current as Record<string, any>,
    );
    if (!resolved) {
      setOptions([]);
      lastResolvedPathRef.current = null;
      return;
    }

    if (resolved === lastResolvedPathRef.current) return;

    lastResolvedPathRef.current = resolved;

    let cancelled = false;
    setIsLoading(true);
    fetchOptionsFromPath(resolved, refDocFieldName).then((fetched) => {
      if (cancelled) return;
      setOptions(fetched);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      lastResolvedPathRef.current = null;
    };
  }, [isActive, refDocPath, dependentValuesKey, refDocFieldName]);

  if (!isActive) {
    return { options: [], isLoading: false };
  }

  return { options, isLoading };
};
